// Regra de desconto do orcamento (dono unico). Ver memoria project_desconto_arredondamento_50.
// O orcamento nasce no preco CARTAO (cheio); o desconto (ate 5%) leva o preco ao pix.
// O total pos-desconto e arredondado PRA CIMA em 3 faixas (sempre em favor da loja —
// desconto e "ate X%", nao "exatamente X%"):
//   - <  R$ 15  -> multiplos de R$ 0,05 (granularidade fina em valores pequenos)
//   - 15..30    -> multiplos de R$ 0,50
//   - >  R$ 30  -> multiplos de R$ 1,00 (sem centavos em vendas maiores)
//
// CALIBRACAO DE CENTAVOS (DA-233): pra o Pix de fora da loja casar SOZINHO com a venda,
// todo orcamento vivo nasce com um total UNICO e com centavo SEMPRE != 0 (nunca ,00). O
// arredondamento acima dava justamente ,00 nas faixas medias/grandes — por isso a calibracao
// SOBE pro proximo centavo livre. O ,00 fica reservado pra pagamentos soltos/parciais na mao.

const EPS = 0.001;

// TOLERANCIA DO CENTAVO DE CALIBRACAO (DA-233/244/299). A calibracao sobe a perna de Pix ate 1
// centavo pra o valor ficar unico; o cliente as vezes paga o REDONDO (72,00 num declarado 72,01),
// deixando ate alguns centavos de residual. Esse residual NAO e falta real — a emissao da nota ja o
// perdoa (emissao-conciliada). Mesma regua tem que valer na CONCILIACAO (senao o centavo perdoado
// pela nota fica cutucando eterno como "Sem recebimento") e no SELO de Vendas hoje. Fonte unica.
export const TOLERANCIA_CENTAVO_CALIBRACAO = 0.05;

/** Arredonda o total pos-desconto PRA CIMA conforme a faixa do valor. */
export function arredondarTotalDesconto(targetRaw: number): number {
  return targetRaw < 15
    ? Math.round((targetRaw + EPS) * 20) / 20
    : targetRaw <= 30
      ? Math.ceil((targetRaw - EPS) * 2) / 2
      : Math.ceil(targetRaw - EPS);
}

/**
 * Piso da grade de desconto — o valor NA GRADE imediatamente <= ao dado (inverso PRA BAIXO do
 * `arredondarTotalDesconto`). Mesmas 3 faixas: 0,05 abaixo de R$15, 0,50 entre 15..30, real inteiro
 * acima de 30. Um valor que JA esta na grade devolve ele mesmo.
 */
export function pisoGradeDesconto(valor: number): number {
  return valor < 15
    ? Math.floor((valor + EPS) * 20) / 20
    : valor <= 30
      ? Math.floor((valor + EPS) * 2) / 2
      : Math.floor(valor + EPS);
}

/**
 * Desfaz o centavo de calibracao (DA-233) de um total que sera pago SEM Pix (dinheiro/cartao/
 * crediario) — a "descalibracao" da ponta solta #1 do caixa.
 *
 * A calibracao so empurra o total PRA CIMA a partir de um valor que caiu na grade (evita ,00 e
 * colisao), e o passo e de 1 centavo. Entao o valor natural pre-calibracao e o PISO DA GRADE logo
 * abaixo — MAS so quando a queda cabe na tolerancia do centavo de calibracao (o bump e sempre
 * pequeno). Se a queda ate a grade for maior que a tolerancia, o total NAO veio de um bump: veio de
 * preco editado por item (ex.: 3 x 12,33 = 36,99, que nao esta na grade) e pagar isso em especie e
 * legitimo — devolve o proprio valor intacto. Valor ja na grade tambem volta intacto (queda zero).
 *
 * Fonte unica (front exibe o total caindo ao vivo quando o operador troca pra Dinheiro; backend
 * reprecifica pra esse alvo na finalizacao). Chamar SO em pedido calibrado (requer_valor_unico) —
 * um total quebrado de pedido nao-calibrado e preco real, nao bump.
 */
export function descalibrarTotalCalibrado(calibrado: number): number {
  const cal = Math.round(calibrado * 100) / 100;
  if (!(cal > 0)) return cal;
  const piso = pisoGradeDesconto(cal);
  const queda = Math.round((cal - piso) * 100) / 100;
  return queda > 0 && queda <= TOLERANCIA_CENTAVO_CALIBRACAO ? piso : cal;
}

/**
 * Arredondamento das 3 faixas com TETO no preco Pix (decisao dele, 05/10/2026). O fly anuncia o
 * `preco_pix` por unidade; sem teto, 5 m a R$ 8,90 davam 44,50 cru e o orcamento cobrava 45,00 —
 * "o cliente reclama na hora da compra". Quando o desconto chega no Pix (cru <= tetoPix), o
 * arredondamento pra cima nunca passa de `tetoPix` (soma de qtd x preco_pix por linha). Desconto
 * menor que o Pix (cru acima do teto) arredonda como sempre. Sem teto (null/<=0) = regra antiga.
 */
export function arredondarTotalDescontoComTetoPix(targetRaw: number, tetoPix: number | null | undefined): number {
  const arredondado = arredondarTotalDesconto(targetRaw);
  if (!(tetoPix != null && tetoPix > 0)) return arredondado;
  const teto = Math.round(tetoPix * 100) / 100;
  return targetRaw <= teto + EPS && arredondado > teto ? teto : arredondado;
}

/**
 * Total final aplicando um desconto percentual sobre o preco cheio (cartao), ja arredondado.
 * pct <= 0 devolve o proprio cheio (sem desconto), arredondado a 2 casas.
 * Usado tanto no front (preview ao vivo no caixa) quanto no back (gravacao oficial),
 * pra que o total exibido bata exatamente com o gravado.
 * `tetoPix` (opcional): ver `arredondarTotalDescontoComTetoPix` — so o orcamento passa.
 */
export function totalComDescontoPct(cheio: number, pct: number, tetoPix?: number | null): number {
  if (!(pct > 0)) return Math.round(cheio * 100) / 100;
  return arredondarTotalDescontoComTetoPix(cheio * (1 - pct / 100), tetoPix);
}

/**
 * Total de um pagamento DIVIDIDO com cartao (DA-076 / refino DA-274).
 *
 * O cartao NAO recebe o desconto de pix: cada real COBRADO no cartao paga o preco CHEIO,
 * e so a fatia paga em pix/dinheiro mantem o desconto. Os dois precos se misturam:
 *
 *   total = fatiaCartao(cheio) + (cheio - fatiaCartao) * (totalPix / cheio)
 *         = totalPix + (cheio - totalPix) * min(1, valorCartao / cheio)
 *
 * `valorCartao` e o quanto sera cobrado no cartao, em preco CHEIO (a soma das linhas de cartao).
 * Casos-limite:
 *   - valorCartao = 0       -> total = totalPix (tudo com desconto)
 *   - valorCartao >= cheio  -> total = cheio    (tudo no cartao, sem desconto)
 *
 * Cada real no cartao devolve exatamente a fatia de desconto daquele real — nada de zerar o
 * desconto inteiro por causa de um cartao parcial. Funcao PURA usada no front (preview/restante
 * ao vivo) E no back (reprecificacao oficial), pra o total exibido bater ao centavo com o gravado
 * (invariante: nota fiscal = soma dos recebimentos). Resultado em 2 casas. Juros de parcelamento
 * NAO entra aqui — vira vOutro por cima (DA-140).
 */
export function totalComCartaoParcial(cheio: number, totalPix: number, valorCartao: number): number {
  if (!(cheio > 0)) return Math.round(totalPix * 100) / 100;
  const frac = Math.min(1, Math.max(0, valorCartao) / cheio);
  return Math.round((totalPix + (cheio - totalPix) * frac) * 100) / 100;
}

/**
 * INVERSO de `totalComCartaoParcial`: dado o total MISTO final desejado (`totalFinal`), o preco
 * cheio e o quanto vai no cartao, devolve o `basePix` (total no NIVEL PIX) que, passado de volta
 * pra `totalComCartaoParcial(cheio, basePix, valorCartao)`, reproduz `totalFinal`.
 *
 * Usado quando o operador da um desconto SO na parte pix/dinheiro num pagamento misto (campos
 * "Desc R$/Desc %" por linha no caixa): a UI conhece o total misto que quer (cartao no cheio +
 * pix/dinheiro ja descontado), mas o backend reprecifica pelo basePix (passo 3.5) e re-infla o
 * cartao (passo 4). Esta funcao traduz um no outro pra o `total_alvo` enviado bater exato.
 *
 * Da formula   totalFinal = basePix*(1-frac) + cheio*frac,  frac = min(1, valorCartao/cheio):
 *   basePix = (totalFinal - cheio*frac) / (1 - frac)
 *
 * Casos-limite:
 *   - valorCartao <= 0   -> nao ha cartao: basePix = totalFinal (nada a inverter).
 *   - valorCartao >= cheio (frac=1) -> tudo no cartao, nao ha parte pix pra descontar: devolve
 *     `cheio` (o total nesse caso e sempre o cheio, independente do basePix).
 * Resultado em 2 casas.
 */
export function basePixParaTotal(cheio: number, totalFinal: number, valorCartao: number): number {
  if (!(cheio > 0) || !(valorCartao > 0)) return Math.round(totalFinal * 100) / 100;
  const frac = Math.min(1, valorCartao / cheio);
  if (frac >= 1) return Math.round(cheio * 100) / 100;
  return Math.round(((totalFinal - cheio * frac) / (1 - frac)) * 100) / 100;
}

/**
 * Divide o pagamento com cartao entre os itens do pedido (backend, passo 4 do caixa.finalizar).
 *
 * Cada item volta de `subAtual` (preco pix) rumo a `subOriginal` (cheio) na fracao coberta pelo
 * cartao (`valorCartao/cheio`), removendo so a fatia de desconto proporcional. O residual de
 * arredondamento cai no ULTIMO item pra soma bater EXATO o alvo — a nota fecha ao centavo com os
 * pagamentos (invariante nota = soma dos recebimentos; trava 4.9). `total` = mesmo numero da regra
 * unica `totalComCartaoParcial`, o que o front exibe e coleta. Juros de parcelamento fica de fora
 * (entra como vOutro por cima, DA-140).
 */
export function dividirPagamentoCartaoNosItens(
  itens: Array<{ subOriginal: number; subAtual: number }>,
  valorCartao: number,
): { subtotais: number[]; total: number } {
  // O "cheio" de cada item nunca fica ABAIXO do que ja esta sendo cobrado (subAtual): se a operadora
  // subiu o preco acima do preco_original (acrescimo no orcamento, ex. 4,21 -> 5,00), o cheio
  // congelado da tabela nao pode puxar o total PRA BAIXO ao pagar no cartao — furava a trava de
  // fechamento (pago > total => acima_teto) e barrava uma venda legitima. Espelha o clamp do front
  // (`cheio = max(subtotal_bruto, total)`). Caso normal (cheio >= pix) fica intacto (no-op).
  const linhas = itens.map((it) => ({
    subOriginal: Math.max(it.subOriginal, it.subAtual),
    subAtual: it.subAtual,
  }));
  const cheioTotal = Math.round(linhas.reduce((s, it) => s + it.subOriginal, 0) * 100) / 100;
  const pixTotal = Math.round(linhas.reduce((s, it) => s + it.subAtual, 0) * 100) / 100;
  const total = totalComCartaoParcial(cheioTotal, pixTotal, valorCartao);
  const frac = cheioTotal > 0 ? Math.min(1, valorCartao / cheioTotal) : 0;
  const subtotais = linhas.map((it) => {
    const desc = Math.max(0, it.subOriginal - it.subAtual);
    return Math.round((it.subAtual + desc * frac) * 100) / 100;
  });
  if (subtotais.length > 0) {
    const soma = Math.round(subtotais.reduce((s, v) => s + v, 0) * 100) / 100;
    const diff = Math.round((total - soma) * 100) / 100;
    if (diff !== 0) {
      const last = subtotais.length - 1;
      subtotais[last] = Math.round((subtotais[last]! + diff) * 100) / 100;
    }
  }
  return { subtotais, total };
}

/**
 * Preco CHEIO (de tabela) de UMA linha do pedido, em 2 casas (DA-785, refina DA-418).
 *
 * Linha SEM desconto (preco_unitario = preco_original) cujo subtotal gravado difere da conta crua
 * por MENOS de 1 centavo: o cheio E' o subtotal gravado. Recalcular `round(preco * qtd)` aqui da'
 * o meio centavo pro lado errado quando o preco tem 4 casas e cai exatamente no meio
 * (136,8250 virou 136,82 num caminho que gravou e 136,83 no recalculo) — e quem compara cheio x
 * total le esse centavo como "desconto de pix" que nao existe. Diferenca de 1 centavo pra cima e'
 * reducao de verdade (ex.: 19 x 2,11 = 40,09 gravado 40,00) e continua contando como desconto.
 * Gemea SQL: `cheioLinhaSql` no backend — as duas tem que mudar juntas.
 */
export function subtotalCheioDaLinha(l: {
  precoOriginal: number;
  precoUnitario: number;
  quantidade: number;
  subtotal: number;
}): number {
  const cru = l.precoOriginal * l.quantidade;
  if (l.precoUnitario === l.precoOriginal && Math.abs(l.subtotal - cru) < 0.01) return l.subtotal;
  return Math.round(cru * 100) / 100;
}

// --- Acrescimo no fechamento (gemeo do desconto DA-202) ---
//
// A trava anti-nota-furada (DA-076) recusa QUALQUER diferenca entre o valor cobrado e o total
// da venda. Isso barra o caso legitimo de a operadora cobrar A MAIS de proposito (ajuste de
// preco pra cima / arredondamento). O acrescimo libera cobrar ACIMA do total ate um teto de
// 10%, tratando a diferenca como preco real (a mercadoria custou mais) — os itens sobem
// proporcionalmente pra a nota fechar (soma dos itens = total = pagamento). Cobrar A MENOS
// que o total continua barrado (e o bug original do pix-no-cartao); acima de 10% tambem barra
// (protege contra erro de digitacao, ex.: 350 numa venda de 33).

/** Teto de acrescimo em % sobre o total (10% pra qualquer valor, sem excecao). */
export const TETO_ACRESCIMO_PCT = 10;

export type ClassePagamentoFechamento = 'ok' | 'acrescimo' | 'acima_teto' | 'abaixo';

/**
 * Classifica a diferenca entre o valor pago (soma dos pagamentos) e o total da venda no
 * fechamento. Regra unica usada no FRONT (libera/bloqueia o Finalizar + aviso) e no BACKEND
 * (trava 4.9). Tolerancia de centavo (±0,05) — a mesma da invariante da NFC-e/NF-e.
 *
 *  - 'ok'        : pago == total (dentro da tolerancia) -> fluxo normal
 *  - 'acrescimo' : pago acima do total, ate o teto (default 10%) -> reprecifica os itens PRA CIMA
 *  - 'acima_teto': pago acima do total + teto -> REJEITA (erro de digitacao)
 *  - 'abaixo'    : pago abaixo do total -> REJEITA (caso pix-no-cartao, DA-076)
 *
 * O teto de 10% e medido sobre o `total` (o total ja computado da venda, apos desconto/cartao).
 */
export function classificarPagamentoFechamento(
  total: number,
  pago: number,
  tetoPct: number = TETO_ACRESCIMO_PCT,
): ClassePagamentoFechamento {
  const gap = Math.round((pago - total) * 100) / 100;
  if (gap < -0.05) return 'abaixo';
  if (gap <= 0.05) return 'ok';
  const teto = Math.round(total * (tetoPct / 100) * 100) / 100;
  if (gap > teto + 0.05) return 'acima_teto';
  return 'acrescimo';
}

/**
 * True quando um total EDITADO passa do teto de acrescimo (default 10%) acima do preco cheio
 * (preco de tabela). Usado no caminho do campo "Total" (front `aplicarTotalAlvo` e backend passo
 * 3.5): editar o total pra cima e um acrescimo de preco real, mas nao pode passar de 10% acima da
 * tabela — o mesmo teto do overpagamento (passo 4.9), so que aqui a base e o cheio. Desconto
 * (total < cheio) nunca dispara. Protege contra erro de digitacao (ex.: 350 numa venda de 33).
 */
export function acrescimoAcimaDoTeto(
  cheio: number,
  total: number,
  tetoPct: number = TETO_ACRESCIMO_PCT,
): boolean {
  if (!(cheio > 0)) return false;
  const limite = Math.round(cheio * (1 + tetoPct / 100) * 100) / 100;
  return total > limite + 0.05;
}

/**
 * Rateia um total-alvo entre os itens PROPORCIONALMENTE ao peso atual de cada um, com o
 * residual de arredondamento no ULTIMO item pra somar EXATO o alvo (mesma maquina de rateio-
 * centavo que `dividirPagamentoCartaoNosItens`). Usado no ACRESCIMO de fechamento: quando o
 * cliente paga acima do total, os itens sobem proporcionalmente pra a nota fechar (soma dos
 * itens = total = pagamento). `pesos` = subtotal ATUAL de cada item (ja pos-desconto/cartao),
 * que preserva as proporcoes relativas. alvo/resultado em 2 casas. Peso total <= 0 devolve zeros.
 */
export function ratearTotalNosItens(pesos: number[], alvo: number): number[] {
  const soma = Math.round(pesos.reduce((s, v) => s + v, 0) * 100) / 100;
  if (!(soma > 0)) return pesos.map(() => 0);
  const alvo2 = Math.round(alvo * 100) / 100;
  const subs = pesos.map((p) => Math.round(alvo2 * (p / soma) * 100) / 100);
  const somaSubs = Math.round(subs.reduce((s, v) => s + v, 0) * 100) / 100;
  const diff = Math.round((alvo2 - somaSubs) * 100) / 100;
  if (diff !== 0 && subs.length > 0) {
    const last = subs.length - 1;
    subs[last] = Math.round((subs[last]! + diff) * 100) / 100;
  }
  return subs;
}

// --- Calibracao de centavos pra conciliacao automatica do Pix (DA-233) ---

/** Centavos inteiros do valor (0..99). Robusto a ruido de ponto flutuante. */
function centavos(valor: number): number {
  return Math.round(valor * 100) % 100;
}

/** true quando o total termina em centavo != 0 (calibravel). ,00 e reservado. */
export function temCentavoQuebrado(valor: number): boolean {
  return centavos(Math.round(valor * 100) / 100) !== 0;
}

/**
 * Calibra um total cru pra um valor com centavo SEMPRE != 0, SEMPRE DIFERENTE DO CRU e UNICO
 * entre os "tomados".
 *
 * Regras (DA-233, revisao DA-663):
 *  - direcao PRA CIMA (a favor da loja): o resultado e > que o cru (em 2 casas).
 *  - nunca termina em ,00: se o passo cair num redondo, sobe 1 centavo.
 *  - unico: nao colide com nenhum valor em `tomados` (linhas de Pix dos outros pedidos vivos).
 *  - sobe de 1 em 1 centavo, A PARTIR DE cru + 1, ate achar um centavo quebrado e livre.
 *
 * ⚠️ O "sempre sobe" e a revisao de 2026-09-03 (DA-663) e reverte a politica anterior ("quebrado
 * ja livre fica intacto, nao cobra centavo a toa"). Motivo medido: o valor que o carrinho produz
 * sozinho e um valor DE COMERCIO — em 60 dias os quatro casamentos automaticos por valor foram
 * 26,50 / 18,50 / 8,50 / 9,50, e tres deles em valores que se REPETEM entre Pix de clientes
 * diferentes (26,50 apareceu 5x). "Tem centavo" nao prova "escolhido por nos"; subir pelo menos 1
 * centavo prova, e custa 1 (ou 2, quando o cru termina em ,99 e a subida atravessa o real).
 *
 * `jaCalibrado` e a GUARDA DE IDEMPOTENCIA, e sem ela a regra acima vira churn: quem chama nem
 * sempre traz o valor cru — reenviar o pedido ao caixa soma os subtotais dos itens (que ja foram
 * redistribuidos pro total calibrado) e regerar o QR manda o valor da linha ja subida. Passando o
 * valor que NOS mesmos escolhemos antes, um cru identico a ele volta intacto em vez de subir de
 * novo a cada passada.
 *
 * `tomados` deve conter os valores ja arredondados a 2 casas. Funcao PURA + deterministica
 * (a unicidade real, sob corrida, e garantida pelo indice parcial no banco — esta funcao so
 * escolhe o candidato). Limite de seguranca de 10.000 passos (R$ 100) pra nunca travar.
 */
export function calibrarValorUnico(
  cru: number,
  tomados: Iterable<number> = [],
  jaCalibrado?: number | null,
): number {
  const base = Math.round(cru * 100);
  const setCent = new Set<number>();
  for (const t of tomados) setCent.add(Math.round(t * 100));
  // Idempotencia: o cru JA e o valor que escolhemos antes -> nada a fazer (ver `jaCalibrado` acima).
  // ⚠️ Ela cede pros `tomados`: enquanto este pedido esteve fora do caixa outro pode ter ficado com
  // aquele centavo, e devolve-lo intacto criaria a colisao que a calibracao existe pra impedir.
  if (jaCalibrado != null && Math.round(jaCalibrado * 100) === base
      && base % 100 !== 0 && !setCent.has(base)) {
    return base / 100;
  }
  let cent = base + 1; // sempre acima do cru: o valor cobrado nunca e o que o carrinho produziu
  for (let i = 0; i < 10000; i++) {
    if (cent % 100 !== 0 && !setCent.has(cent)) return cent / 100;
    cent += 1;
  }
  // Fallback impossivel na pratica: 1 centavo acima do cru, pulando o redondo.
  return ((base + 1) % 100 === 0 ? base + 2 : base + 1) / 100;
}

/**
 * Reposiciona o CENTAVO DE CALIBRACAO na perna de PIX do pagamento (DA-299).
 *
 * O centavo quebrado do total (DA-233) existe pra o Pix que chega ser identificavel. Num pagamento
 * MISTO (ex.: dinheiro + Pix) ele tem que morar na perna do PIX — se sobra no dinheiro, o Pix cobrado
 * fica redondo (perde a marca) e a gaveta fecha com centavo, que ninguem paga na pratica. No envio ao
 * caixa isso ja acontece (orcamentos.routes.ts); no FECHAMENTO a perna nova nascia com "o que falta do
 * total" e o centavo caia em quem fosse criada por ultimo — acidente de ordem, nao regra.
 *
 * Move o centavo SO quando o quadro e inequivoco:
 *  - existe exatamente UMA perna de Pix, e ela esta redonda (,00);
 *  - as pernas somam o total (o split esta completo);
 *  - o total tem centavo quebrado (foi calibrado);
 *  - existe uma perna NAO-Pix carregando exatamente esse centavo (foi ela que pegou o resto).
 *
 * Fora disso devolve as linhas INTACTAS — dinheiro com centavo de verdade (cliente pagou 47,53 em
 * especie) nao pode ser arredondado a forca. A soma e sempre preservada.
 *
 * ⛔ E NAO move o centavo pra uma perna de Pix JA CONFIRMADA (`confirmado: true` — Pix vinculado ou
 * pago nesta tela). [DA-585] O centavo existe pra o Pix **que ainda vai chegar** ser identificavel
 * na conciliacao; num Pix que ja chegou nao ha mais nada a identificar, e somar 1 centavo ao valor
 * dele faz a linha AFIRMAR um valor que o banco nao recebeu. O estrago nao e' cosmetico: o cerco da
 * nota ([DA-248]) compara declarado × recebido com regua de **meio centavo**, entao a venda fecha
 * (a trava da venda tolera 5) e a NOTA fica presa — sai depois, pelo servidor, sem painel de
 * impressao pra quem esta no balcao ([DA-584]). O centavo fica na perna de dinheiro, que e' onde o
 * cliente de fato ainda deve.
 */
export function centavoNaPernaPix<T extends { tipo: string; valor: number; confirmado?: boolean }>(linhas: T[], total: number): T[] {
  const centavoTotal = centavos(total);
  if (centavoTotal === 0) return linhas;

  const soma = linhas.reduce((s, l) => s + Math.round(l.valor * 100), 0);
  if (soma !== Math.round(total * 100)) return linhas;

  const idxPix = linhas.reduce<number[]>((acc, l, i) => (l.tipo === 'pix' ? [...acc, i] : acc), []);
  if (idxPix.length !== 1) return linhas;
  const iPix = idxPix[0]!;
  if (linhas[iPix]!.confirmado) return linhas;            // Pix ja recebido — ver o aviso acima (DA-585)
  if (centavos(linhas[iPix]!.valor) !== 0) return linhas; // Pix ja carrega centavo — nada a fazer

  const iDoador = linhas.findIndex((l, i) => i !== iPix && centavos(l.valor) === centavoTotal);
  if (iDoador < 0) return linhas;

  const centDoador = Math.round(linhas[iDoador]!.valor * 100) - centavoTotal;
  if (centDoador <= 0) return linhas; // doador viraria zero/negativo — deixa como esta

  return linhas.map((l, i) => {
    if (i === iPix) return { ...l, valor: (Math.round(l.valor * 100) + centavoTotal) / 100 };
    if (i === iDoador) return { ...l, valor: centDoador / 100 };
    return l;
  });
}

/**
 * Realinha as pernas de pagamento pra somarem EXATAMENTE um total-alvo (DA-321).
 *
 * As pernas nascem no "enviar ao caixa" com o total DAQUELE instante. Se o orcamento e editado
 * depois (desconto/itens), o total muda e a perna nao — e quem le as pernas direto (a nota do
 * Caminho B, a `somaLinhasNaoPix` da calibracao) passa a ver um valor que nao existe mais. Foi
 * assim que o ORC-005895 ficou com total 91,01 e perna 102,01 (o desconto subiu de 4,21% pra
 * 14,54% via PUT, que nunca tocou nas pernas).
 *
 * Quem absorve a diferenca, em ordem:
 *  - perna UNICA: ela E o total, sem ambiguidade;
 *  - misto com Pix: a ULTIMA perna de Pix (mesma regra do envio ao caixa). As pernas nao-Pix sao
 *    valores DECLARADOS pelo cliente ("R$ 50 em dinheiro") — quem e "o resto" por natureza e o Pix;
 *  - sem Pix (ou Pix que ficaria <= 0): rateio proporcional, residual na ultima perna.
 *
 * Alvo <= 0 ou lista vazia devolve as linhas INTACTAS. A soma do resultado e sempre o alvo.
 */
export function realinharPernasParaTotal<T extends { tipo: string; valor: number }>(
  linhas: T[],
  alvo: number,
): T[] {
  if (linhas.length === 0) return linhas;
  const alvoC = Math.round(alvo * 100);
  if (!(alvoC > 0)) return linhas;
  const somaC = linhas.reduce((s, l) => s + Math.round(l.valor * 100), 0);
  const deltaC = alvoC - somaC;
  if (deltaC === 0) return linhas;

  if (linhas.length === 1) return [{ ...linhas[0]!, valor: alvoC / 100 }];

  let iPix = -1;
  for (let i = linhas.length - 1; i >= 0; i--) {
    if (linhas[i]!.tipo === 'pix') { iPix = i; break; }
  }
  if (iPix >= 0) {
    const novoC = Math.round(linhas[iPix]!.valor * 100) + deltaC;
    if (novoC > 0) return linhas.map((l, i) => (i === iPix ? { ...l, valor: novoC / 100 } : l));
  }

  // Pernas zeradas nao tem proporcao pra preservar: o alvo inteiro cai na ultima.
  if (somaC <= 0) {
    return linhas.map((l, i) => (i === linhas.length - 1 ? { ...l, valor: alvoC / 100 } : { ...l, valor: 0 }));
  }
  const novos = ratearTotalNosItens(linhas.map((l) => l.valor), alvoC / 100);
  return linhas.map((l, i) => ({ ...l, valor: novos[i]! }));
}
